import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Magic } from 'magic-sdk';
import Confetti from 'react-confetti';
import Header from '../../components/header';
import Footer from '../../components/footer';
import { api } from '../../api/api';

const TWITCH_CLIENT_ID = process.env.REACT_APP_TWITCH_CLIENT_ID || '';
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI || '';
const MAGIC_PUBLISHABLE_KEY = process.env.REACT_APP_MAGIC_PUBLISHABLE_KEY || 'pk_live_1F4B0B1E50C9D1BE';

const GiveawayRedeemPage = () => {
  const [step, setStep] = useState('loading'); // loading, twitch_login, wallet_connect, set_password, redeem_form
  const [twitchUser, setTwitchUser] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [magic, setMagic] = useState<Magic | null>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [rewardCode, setRewardCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const magicInstance = new Magic(MAGIC_PUBLISHABLE_KEY);
    setMagic(magicInstance);

    const checkSession = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const meResponse = await api.get('/api/users/me');
          setCurrentUser(meResponse.data);
          const { data } = await api.get('/api/giveaways/check-winner-status');
          if (data.isWinner) {
            toast.info('Welcome back! You are a winner! Check your email for the reward code.');
            setStep('redeem_form');
          } else {
            toast.info('Welcome back! You were not a winner this time.');
            setTimeout(() => { window.location.href = '/'; }, 3000);
          }
        } catch (error) {
          localStorage.removeItem('accessToken');
          handleTwitchAuthRedirect();
        }
      } else {
        handleTwitchAuthRedirect();
      }
    };

    const handleTwitchAuthRedirect = () => {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      if (accessToken) {
        const fetchTwitchUser = async () => {
          try {
            const response = await axios.get('https://api.twitch.tv/helix/users', {
              headers: { 'Authorization': `Bearer ${accessToken}`, 'Client-ID': TWITCH_CLIENT_ID },
            });
            const user = response.data.data[0];
            console.log('Twitch user logged in:', user);
            console.log('Twitch user email:', user.email);
            setTwitchUser(user);
            setStep('wallet_connect');
          } catch (error) {
            console.error('Error fetching Twitch user:', error);
            toast.error('Failed to fetch your Twitch profile.');
            setStep('twitch_login');
          }
        };
        fetchTwitchUser();
      } else {
        setStep('twitch_login');
      }
    };

    checkSession();
  }, []);

  const handleTwitchLogin = () => {
    const authUrl = `https://id.twitch.tv/oauth2/authorize?client_id=${TWITCH_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=user:read:email`;
    window.location.href = authUrl;
  };

  const handleWalletConnect = async () => {
    if (!magic) return;
    if (!twitchUser?.email) {
      toast.error('Twitch email not available. Please ensure your Twitch account has a verified email.');
      return;
    }
    setIsLoading(true);
    try {
      await magic.auth.loginWithMagicLink({ email: twitchUser.email });
      const magicUser = await magic.user.getInfo();
      if (!magicUser.publicAddress) throw new Error('Could not get wallet address.');
      console.log('Wallet connected:', magicUser.publicAddress);
      setWalletAddress(magicUser.publicAddress);

      const { data } = await api.post('/api/giveaways/check-user', {
        twitchId: twitchUser.login,
        email: twitchUser.email,
        walletAddress: magicUser.publicAddress,
      });

      if (data.exists && data.hasPassword) {
        await handleProcessUserFlow(undefined, magicUser.publicAddress); // Existing user, no password needed
      } else {
        setStep('set_password');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to connect wallet.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessUserFlow = async (password?: string, newWalletAddress?: string) => {
    setIsLoading(true);
    toast.info('Processing your account...');
    try {
      const { data } = await api.post('/api/giveaways/process-user-flow', {
        twitchId: twitchUser.login,
        email: twitchUser.email,
        walletAddress: newWalletAddress || walletAddress,
        password,
        displayName: twitchUser.display_name,
      });

      localStorage.setItem('accessToken', data.accessToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      setCurrentUser(data.user);
      toast.success('Login successful!');

      if (data.isWinner) {
        toast.info('Congratulations! You are a winner! Check your email for the reward code.');
        setStep('redeem_form');
      } else {
        toast.info('You were not a winner this time, but welcome to iPlayMore!');
        setStep('non_winner_message');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An unknown error occurred.';
      toast.error(`Processing failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    handleProcessUserFlow(password);
  }

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    const twitchId = twitchUser?.login || currentUser?.twitch_id;
    if (!twitchId) {
      toast.error("Could not determine Twitch ID to redeem reward.");
      return;
    }

    setIsLoading(true);
    toast.info('Attempting to redeem reward...');
    try {
      const response = await api.post('/api/giveaways/redeem', {
        rewardCode,
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Reward claimed successfully!');
        setShowConfetti(true);
        const { asset, accessToken } = response.data;
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        }
        setTimeout(() => { window.location.href = `/reward-claim?rewardId=${asset.reward_id}`; }, 4000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An unknown error occurred.';
      if (errorMessage.includes('Admin accounts cannot redeem')) {
        toast.error('Admin accounts are not eligible for giveaway redemptions. Please log in with a regular user account.');
      } else {
        toast.error(`Redemption failed: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsLoading(true);
    toast.info('Resending email...');
    try {
      const { data } = await api.post('/api/giveaways/resend-reward-email');
      if (data.success) {
        toast.success(data.message);
        console.log(`Resend email status: ${data.success}, Email: ${currentUser?.email}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An unknown error occurred.';
      toast.error(`Failed to resend email: ${errorMessage}`);
      console.log(`Resend email status: false, Email: ${currentUser?.email}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'loading':
        return <p>Loading...</p>;
      case 'twitch_login':
        return (
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Step 1: Connect Twitch</h1>
            <button onClick={handleTwitchLogin} className="w-full px-4 py-2 font-semibold text-white bg-purple-600 rounded-md hover:bg-purple-700">
              Login with Twitch
            </button>
          </div>
        );
      case 'wallet_connect':
        return (
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Step 2: Connect Wallet</h1>
            <button onClick={handleWalletConnect} disabled={isLoading} className="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {isLoading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          </div>
        );
      case 'set_password':
        return (
          <form onSubmit={handleSetPassword} className="space-y-6">
            <h1 className="text-3xl font-bold text-center">Step 3: Secure Your Account</h1>
            <p className="text-center">Set a password for your iPlayMore account to continue.</p>
            <div>
              <label htmlFor="password" className="block text-sm font-medium">Password</label>
              <input id="password" name="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 mt-1 text-white bg-gray-700 border border-gray-600 rounded-md" placeholder="••••••••" />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium">Confirm Password</label>
              <input id="confirmPassword" name="confirmPassword" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 mt-1 text-white bg-gray-700 border border-gray-600 rounded-md" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={isLoading} className="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {isLoading ? 'Processing...' : 'Continue'}
            </button>
          </form>
        );
      case 'redeem_form':
        return (
          <form onSubmit={handleRedeem} className="space-y-6">
            <h1 className="text-3xl font-bold text-center">Claim Your Reward</h1>
            <p className="text-center">Please check your email for your 16-digit reward code and enter it below.</p>
            <div>
              <label htmlFor="rewardCode" className="block text-sm font-medium">16-Digit Reward Code</label>
              <input id="rewardCode" name="rewardCode" type="text" required value={rewardCode} onChange={(e) => setRewardCode(e.target.value)} maxLength={16} className="w-full px-3 py-2 mt-1 text-white bg-gray-700 border border-gray-600 rounded-md" placeholder="0000-0000-0000-0000" />
              <p className="text-xs text-gray-400 mt-2">If the email was sent, please also check your spam folder.</p>
            </div>
            <div className="text-center mt-4">
              <button type="button" onClick={handleResendEmail} className="text-sm text-blue-400 hover:underline">
                Resend Email
              </button>
            </div>
            <button type="submit" disabled={isLoading} className="w-full px-4 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
              {isLoading ? 'Claiming...' : 'Claim Reward'}
            </button>
          </form>
        );
      case 'non_winner_message':
        return (
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-bold mb-4">Welcome to iPlayMore!</h1>
            <p>You were not a winner this time, but we're glad to have you.</p>
            <button onClick={() => { window.location.href = '/'; }} className="w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700">
              Go to Homepage
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundImage: "url(/assets/img/auth/bg.png)", backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center" }} className={`relative w-full min-h-screen flex flex-col items-center text-white`}>
      {showConfetti && <Confetti />}
      <Header />
      <div className="w-full md:w-[676px] h-full flex flex-col items-center mt-5 z-10 mb-[166px]">
        <img alt="modal-logo" src="/assets/img/login/logo.png" className="w-[252px] md:w-[276px] mb-8" />
        <div className="w-full max-w-md p-8 space-y-6">
          {renderStep()}
        </div>
      </div>
      <div className="md:w-[676px] w-full h-full absolute bg-black bg-opacity-93"></div>
      <div className="w-full flex absolute bottom-0">
        <Footer type="sm" />
      </div>
    </div>
  );
};

export default GiveawayRedeemPage;
